import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { colors } from '../../utils/colors';
import aboutMeImage from '../../assets/about_me_image.jpg';
import aboutMeImageWebp from '../../assets/about_me_image.webp';

const AboutSection = styled.section`
  padding: 6rem 1rem;
  background: ${colors.backgroundLight};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(to right, transparent, ${colors.border}, transparent);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const ImageWrapper = styled(motion.div)`
  position: relative;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 20px 40px ${colors.shadow};
  height: 700px;
  width: 75%;

  img {
    width: 100%;
    height: 140%;
    object-fit: cover;
    transform: translateY(-30%);
  }

  @media (max-width: 768px) {
    height: 400px;
    width: 60%;
    margin: 0 auto;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, ${colors.primary}4D, transparent);
    z-index: 1;
  }
`;

const Content = styled(motion.div)`
  h2 {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
    color: ${colors.textDark};
  }

  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: ${colors.textLight};
    margin-bottom: 1.5rem;
  }
`;

const Quote = styled.span`
  font-family: 'Dancing Script', 'Brush Script MT', 'Lucida Handwriting', cursive;
  font-size: 1.5rem;
  color: ${colors.primary};
  font-weight: 400;
  text-align: center;
  display: block;
  margin-top: 2rem;
  line-height: 1.4;
`;

export const AboutMe = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <AboutSection id="sobre-mi" ref={ref}>
      <Container>
        <ImageWrapper
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <picture>
            <source srcSet={aboutMeImageWebp} type="image/webp" />
            <img
              src={aboutMeImage}
              alt="Psicóloga Andrea Dávila en su consultorio en Pocitos, Montevideo"
              width="2268"
              height="4032"
              loading="lazy"
            />
          </picture>
        </ImageWrapper>

        <Content
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <h2>Sobre Mí</h2>
          <p>
            Soy psicóloga especializada en terapia cognitivo-conductual y EMDR.
            Cuento con experiencia en la atención clínica de adolescentes y adultos,
            acompañando procesos relacionados con la ansiedad,
            depresión, duelos y crecimiento personal.
          </p>
          <p>
            Mi objetivo es brindar un espacio seguro y de confianza donde cada persona
            pueda explorar sus desafíos, encontrar recursos internos y avanzar hacia el
            bienestar emocional. Creo firmemente en la importancia de una conexión auténtica
            y empática en el proceso terapéutico, integrando técnicas basadas en la evidencia
            con un compromiso genuino hacia cada paciente.
          </p>

          <Quote>
            "Lo que niegas te somete, lo que aceptas te transforma"
          </Quote>
        </Content>
      </Container>
    </AboutSection>
  );
};
